import React from 'react';
import { Select, select, t, Trans } from '@lingui/macro';

import FrequentlyAskedQuestions from '@rover/components/src/FrequentlyAskedQuestions';
import { Link, Paragraph } from '@rover/kibble/core';

import { ServiceFaq } from './constants';
import FirstFAQIsOpen from './FirstFAQIsOpen';
import FrequentlyAskedQuestion from './FrequentlyAskedQuestion';
import useFacts from './useFacts';

const CatSittingFaqs: React.FC<ServiceFaq> = ({
  facts,
  locationName,
  localizedLinkUrls: links,
  localityType,
  htmlRef,
}) => {
  // we create these two variables so translators can distinguish between them in the lingui selects
  const cityName = locationName;
  const neighborhoodName = locationName;

  const {
    avgRepeatCustomerAmongSittersWithRepeatNum,
    maxSearchableSittersNum,
    medianListPricePerUnitLocalCurrencyNum,
    sittersWithMinResponseTimeIn60minPct,
    sittersCanProvideDailyExercisePct,
    sittersCanProvideMedicationPct,
    sittersCanProvideSpecialCarePct,
    yearsOfExperienceNum,
  } = useFacts(facts);

  return (
    <FrequentlyAskedQuestions
      htmlRef={htmlRef}
      data-testid="cat-sitting-faqs"
      mt={['6x', '16x']}
      pb={['4x', '8x']}
    >
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        <Trans>Cat Sitting FAQs</Trans>
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        <Trans>Frequently asked questions about Rover</Trans>
      </FrequentlyAskedQuestions.Subheader>
      <FirstFAQIsOpen>
        {/* 1 */}
        {medianListPricePerUnitLocalCurrencyNum &&
          'whatAreTheServiceFees' in links &&
          (() => {
            const { yearMonthLastUpdated, asCurrency: formattedAmount } =
              medianListPricePerUnitLocalCurrencyNum;
            return (
              <FrequentlyAskedQuestion
                question={select(localityType, {
                  city: `How much does it cost to hire a cat sitter in ${cityName}?`,
                  neighborhood: `How much does it cost to hire a cat sitter in ${neighborhoodName}?`,
                  other: `How much does it cost to hire a ${locationName} cat sitter?`,
                })}
              >
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        Cat sitters on Rover are free to set their rates. The median cost to hire a
                        cat sitter in {cityName} on Rover as of {yearMonthLastUpdated} was about{' '}
                        {formattedAmount} per night, after factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A cat sitter’s rate may also change as you customize your booking to fit
                        your and your cat’s needs.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Cat sitters on Rover are free to set their rates. The median cost to hire a
                        cat sitter in {neighborhoodName} on Rover as of {yearMonthLastUpdated} was
                        about {formattedAmount} per night, after factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A cat sitter’s rate may also change as you customize your booking to fit
                        your and your cat’s needs.
                      </Trans>
                    }
                    other={
                      <Trans>
                        Cat sitters on Rover are free to set their rates. The median cost to hire a
                        {locationName} cat sitter on Rover as of {yearMonthLastUpdated} was about{' '}
                        {formattedAmount} per night, after factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A cat sitter’s rate may also change as you customize your booking to fit
                        your and your cat’s needs.
                      </Trans>
                    }
                  />
                </Paragraph>
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 2 */}
        {'howDoIContactAPetSitterOrDogWalkerRoverApp' in links &&
          'howDoIContactAPetSitterOrDogWalkerComputer' in links && (
            <FrequentlyAskedQuestion question={t`How do I contact a ${locationName} cat sitter?`}>
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  <Trans>
                    If you’re searching for a {locationName} cat sitter for the first time, visit
                    the cat sitter’s profile and select the Contact button.
                  </Trans>
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    If you have an active request or have booked a service with a cat sitter before,
                    learn more about how to do this in the{' '}
                    <Link href={links.howDoIContactAPetSitterOrDogWalkerRoverApp} target="_blank">
                      Rover app
                    </Link>{' '}
                    or on your{' '}
                    <Link href={links.howDoIContactAPetSitterOrDogWalkerComputer} target="_blank">
                      computer
                    </Link>
                    .
                  </Trans>
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}

        {/* 3 */}
        {maxSearchableSittersNum &&
          (() => {
            const { yearMonthLastUpdated, asNumber: catSittersCount } = maxSearchableSittersNum;
            return (
              <FrequentlyAskedQuestion
                question={select(localityType, {
                  city: `How many cat sitters are in ${cityName}?`,
                  neighborhood: `How many cat sitters are in ${neighborhoodName}?`,
                  other: `How many ${locationName} cat sitters are there?`,
                })}
              >
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        As of {yearMonthLastUpdated}, there are {catSittersCount} cat sitters in{' '}
                        {cityName}. You can filter, sort, expand your radius, read reviews, and
                        compare pricing to find the perfect cat sitter near you. As a reminder, cat
                        sitters joining Rover must pass a background check for you and your cat’s
                        safety.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        As of {yearMonthLastUpdated}, there are {catSittersCount} cat sitters in{' '}
                        {neighborhoodName}. You can filter, sort, expand your radius, read reviews,
                        and compare pricing to find the perfect cat sitter near you. As a reminder,
                        cat sitters joining Rover must pass a background check for you and your
                        cat’s safety.
                      </Trans>
                    }
                    other={
                      <Trans>
                        As of {yearMonthLastUpdated}, there are {catSittersCount} {locationName} cat
                        sitters. You can filter, sort, expand your radius, read reviews, and compare
                        pricing to find the perfect cat sitter near you. As a reminder, cat sitters
                        joining Rover must pass a background check for you and your cat’s safety.
                      </Trans>
                    }
                  />
                </Paragraph>
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 4 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `How long will it take for me to find a cat sitter in ${cityName}?`,
            neighborhood: `How long will it take for me to find a cat sitter in ${neighborhoodName}?`,
            other: `How long will it take for me to find a ${locationName} cat sitter?`,
          })}
        >
          {sittersWithMinResponseTimeIn60minPct ? (
            (() => {
              const percent = sittersWithMinResponseTimeIn60minPct.asPercent;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    Rover makes it easy to reach out to multiple cat sitters about your booking.
                    Typically, {percent} of {locationName} cat sitters respond in under an hour.
                  </Trans>
                </Paragraph>
              );
            })()
          ) : (
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                Rover makes it easy to reach out to multiple cat sitters about your booking.
                Typically, cat sitters on Rover respond in under an hour.
              </Trans>
            </Paragraph>
          )}
        </FrequentlyAskedQuestion>

        {/* 5 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `How experienced are cat sitters in ${cityName}?`,
            neighborhood: `How experienced are cat sitters in ${neighborhoodName}?`,
            other: `How experienced are ${locationName} cat sitters?`,
          })}
        >
          {(() => {
            if (yearsOfExperienceNum && avgRepeatCustomerAmongSittersWithRepeatNum) {
              const yearsOfExperience = yearsOfExperienceNum.raw;
              const repeatCustomersCount = avgRepeatCustomerAmongSittersWithRepeatNum.asNumber;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        Cat sitters in {cityName} have an average of {yearsOfExperience} years of
                        experience. Cat sitters with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from cat sitter
                        to cat sitter, but you can view reviews, years of experience, and number of
                        repeat clients when you compare cat sitters in {cityName}.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Cat sitters in {neighborhoodName} have an average of {yearsOfExperience}{' '}
                        years of experience. Cat sitters with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from cat sitter
                        to cat sitter, but you can view reviews, years of experience, and number of
                        repeat clients when you compare cat sitters in {neighborhoodName}.
                      </Trans>
                    }
                    other={
                      <Trans>
                        {locationName} cat sitters have an average of {yearsOfExperience} years of
                        experience. Cat sitters with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from cat sitter
                        to cat sitter, but you can view reviews, years of experience, and number of
                        repeat clients when you compare cat sitters.
                      </Trans>
                    }
                  />
                </Paragraph>
              );
            }

            if (avgRepeatCustomerAmongSittersWithRepeatNum) {
              const repeatCustomersCount = avgRepeatCustomerAmongSittersWithRepeatNum.asNumber;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        Cat sitters with repeat customers have an average of {repeatCustomersCount}{' '}
                        repeat clients. Experience can vary from cat sitter to cat sitter, but you
                        can view reviews, years of experience, and number of repeat clients when you
                        compare cat sitters in {cityName}.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Cat sitters with repeat customers have an average of {repeatCustomersCount}{' '}
                        repeat clients. Experience can vary from cat sitter to cat sitter, but you
                        can view reviews, years of experience, and number of repeat clients when you
                        compare cat sitters in {neighborhoodName}.
                      </Trans>
                    }
                    other={
                      <Trans>
                        Cat sitters with repeat customers have an average of {repeatCustomersCount}{' '}
                        repeat clients. Experience can vary from cat sitter to cat sitter, but you
                        can view reviews, years of experience, and number of repeat clients when you
                        compare cat sitters.
                      </Trans>
                    }
                  />
                </Paragraph>
              );
            }

            if (yearsOfExperienceNum) {
              const yearsOfExperience = yearsOfExperienceNum.raw;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        Cat sitters in {cityName} have an average of {yearsOfExperience} years of
                        experience. This can vary widely from cat sitter to cat sitter, but you can
                        view reviews, years of experience, and number of repeat clients when
                        comparing cat sitters available in {cityName}.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        Cat sitters in {neighborhoodName} have an average of {yearsOfExperience}{' '}
                        years of experience. This can vary widely from cat sitter to cat sitter, but
                        you can view reviews, years of experience, and number of repeat clients when
                        comparing cat sitters available in {neighborhoodName}.
                      </Trans>
                    }
                    other={
                      <Trans>
                        {locationName} cat sitters have an average of {yearsOfExperience} years of
                        experience. This can vary widely from cat sitter to cat sitter, but you can
                        view reviews, years of experience, and number of repeat clients when
                        comparing cat sitters.
                      </Trans>
                    }
                  />
                </Paragraph>
              );
            }

            return (
              <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                <Select
                  value={localityType}
                  _city={
                    <Trans>
                      Experience can vary widely from cat sitter to cat sitter, but you can view
                      reviews, years of experience, and number of repeat clients when comparing cat
                      sitters available in {cityName}.
                    </Trans>
                  }
                  _neighborhood={
                    <Trans>
                      Experience can vary widely from cat sitter to cat sitter, but you can view
                      reviews, years of experience, and number of repeat clients when comparing cat
                      sitters available in {neighborhoodName}.
                    </Trans>
                  }
                  other={
                    <Trans>
                      Experience can vary widely from cat sitter to cat sitter, but you can view
                      reviews, years of experience, and number of repeat clients when comparing cat
                      sitters.
                    </Trans>
                  }
                />
              </Paragraph>
            );
          })()}
        </FrequentlyAskedQuestion>

        {/* 6 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `What sort of services do cat sitters in ${cityName} offer on Rover?`,
            neighborhood: `What sort of services do cat sitters in ${neighborhoodName} offer on Rover?`,
            other: `What sort of services do ${locationName} cat sitters offer on Rover?`,
          })}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Do you simply need someone to stop by and play, feed, and scoop? {locationName} cat
              sitters are happy to take care of your cat while you’re at work, on vacation, or
              unavailable for the day, even if all you need is a quick drop-in! Your cat sitter will
              stop by to feed, play with, and snuggle your cat as many times a day as you like. And
              the best part? Your cat gets to stay on their home turf.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 7 */}
        {maxSearchableSittersNum &&
          (() => {
            const catSittersCount = maxSearchableSittersNum.asNumber;
            return (
              <FrequentlyAskedQuestion
                question={t`My cat has special needs. Can I find cat sitting on Rover that can accommodate my cat’s needs?`}
              >
                {sittersCanProvideDailyExercisePct &&
                sittersCanProvideMedicationPct &&
                sittersCanProvideSpecialCarePct ? (
                  (() => {
                    const dailyExercisePercent = sittersCanProvideDailyExercisePct.asPercent;
                    const provideMedicationPercent = sittersCanProvideMedicationPct.asPercent;
                    const specialCarePercent = sittersCanProvideSpecialCarePct.asPercent;
                    return (
                      <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                        <Select
                          value={localityType}
                          _city={
                            <Trans>
                              There are {catSittersCount} cat sitters in {cityName} on Rover, so
                              you’re sure to find the perfect match for your cat! In {cityName},{' '}
                              {dailyExercisePercent} of cat sitters offer daily exercise,{' '}
                              {provideMedicationPercent} offer medication administration, and{' '}
                              {specialCarePercent} have experience with senior cats and cats with
                              special needs. We know each cat is unique and may have specific care
                              instructions. You can filter, sort, expand your radius, read reviews,
                              and compare pricing to find the perfect cat sitter near you. As a
                              reminder, cat sitters joining Rover must pass a background check for
                              you and your cat’s safety.
                            </Trans>
                          }
                          _neighborhood={
                            <Trans>
                              There are {catSittersCount} cat sitters in {neighborhoodName} on
                              Rover, so you’re sure to find the perfect match for your cat! In{' '}
                              {neighborhoodName}, {dailyExercisePercent} of cat sitters offer daily
                              exercise, {provideMedicationPercent} offer medication administration,
                              and {specialCarePercent} have experience with senior cats and cats
                              with special needs. We know each cat is unique and may have specific
                              care instructions. You can filter, sort, expand your radius, read
                              reviews, and compare pricing to find the perfect cat sitter near you.
                              As a reminder, cat sitters joining Rover must pass a background check
                              for you and your cat’s safety.
                            </Trans>
                          }
                          other={
                            <Trans>
                              There are {catSittersCount} {locationName} cat sitters on Rover, so
                              you’re sure to find the perfect match for your cat!{' '}
                              {dailyExercisePercent} of {locationName} cat sitters offer daily
                              exercise, {provideMedicationPercent} offer medication administration,
                              and {specialCarePercent} have experience with senior cats and cats
                              with special needs. We know each cat is unique and may have specific
                              care instructions. You can filter, sort, expand your radius, read
                              reviews, and compare pricing to find the perfect cat sitter near you.
                              As a reminder, cat sitters joining Rover must pass a background check
                              for you and your cat’s safety.
                            </Trans>
                          }
                        />
                      </Paragraph>
                    );
                  })()
                ) : (
                  <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                    <Select
                      value={localityType}
                      _city={
                        <Trans>
                          There are {catSittersCount} cat sitters in {cityName} on Rover, so you’re
                          sure to find the perfect match for your cat! We understand that each cat
                          is unique and may require specific instructions. You can filter, sort,
                          expand your radius, read reviews, and compare pricing to find the perfect
                          cat sitter near you. As a reminder, cat sitters joining Rover must pass a
                          background check for you and your cat’s safety.
                        </Trans>
                      }
                      _neighborhood={
                        <Trans>
                          There are {catSittersCount} cat sitters in {neighborhoodName} on Rover, so
                          you’re sure to find the perfect match for your cat! We understand that
                          each cat is unique and may require specific instructions. You can filter,
                          sort, expand your radius, read reviews, and compare pricing to find the
                          perfect cat sitter near you. As a reminder, cat sitters joining Rover must
                          pass a background check for you and your cat’s safety.
                        </Trans>
                      }
                      other={
                        <Trans>
                          There are {catSittersCount} {locationName} cat sitters on Rover, so you’re
                          sure to find the perfect match for your cat! We understand that each cat
                          is unique and may require specific instructions. You can filter, sort,
                          expand your radius, read reviews, and compare pricing to find the perfect
                          cat sitter near you. As a reminder, cat sitters joining Rover must pass a
                          background check for you and your cat’s safety.
                        </Trans>
                      }
                    />
                  </Paragraph>
                )}
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 8 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `Do cat sitters in ${cityName} pass a background check?`,
            neighborhood: `Do cat sitters in ${neighborhoodName} pass a background check?`,
            other: `Do ${locationName} cat sitters pass a background check?`,
          })}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Yes! Cat sitters joining Rover must pass a background check before offering their
              services. You can also easily keep in touch with your cat sitter through messages on
              Rover for delightful photo updates. The Rover team is available 24/7, and your cat
              sitter has access to advice from qualified veterinary professionals. In the rare event
              of any issues during a booking, rest easy knowing your cat is covered by the Rover
              Guarantee reimbursement program for eligible veterinary care.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 9 */}
        {'howDoICancelABookingAndReceiveARefundRoverApp' in links &&
          'howDoICancelABookingAndReceiveARefundComputer' in links && (
            <FrequentlyAskedQuestion
              question={t`How do I cancel a cat sitting service and receive a refund?`}
            >
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  <i>
                    <Trans>
                      <strong>Note:</strong> If your cat sitter requested to cancel this booking,
                      then they need to cancel from their account. Please contact them via{' '}
                      <Link href="/account/inbox/" target="_blank">
                        Rover Messages
                      </Link>
                      .
                    </Trans>
                  </i>
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    We get it—sometimes plans change and you may need to cancel your cat sitting
                    service. You can cancel an upcoming booking directly from the{' '}
                    <Link
                      href={links.howDoICancelABookingAndReceiveARefundRoverApp}
                      target="_blank"
                    >
                      Rover app
                    </Link>{' '}
                    or your{' '}
                    <Link
                      href={links.howDoICancelABookingAndReceiveARefundComputer}
                      target="_blank"
                    >
                      computer
                    </Link>
                    , in accordance with your cat sitter’s cancellation policy.
                  </Trans>
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}
      </FirstFAQIsOpen>
    </FrequentlyAskedQuestions>
  );
};

export default CatSittingFaqs;
